<template>
  <Modal
    title="Merge Addresses"
    submit-btn-text="Merge"
    @hide-modal="$emit('hide-modal')"
    @submit="handleSubmit"
  >
    <div class="flex flex-col gap-4 md:flex-row">
      <div class="d-flex flex-column full-width">
        <CustomerAddressSelect
          v-model="destinationId"
          name="destinationId"
          label="Destination address"
          :filter="(address) => address.id !== duplicateId"
          class="full-width"
          :customer="currentUser"
          required
          :vee-validate-errors="v$.$errors"
          @selected-address="(address) => (destinationAddress = address)"
        />
        <Transition>
          <div v-if="destinationAddress" class="card p-3 full-width mt-3">
            <div>
              <b>{{ getAddressName(destinationAddress) }}</b>
            </div>
            <AddressLabel :address="destinationAddress" location-type />
            <AddressTaxLabel class="mt-2" :address="destinationAddress" />
            <div class="mt-2 text-sm text-gray-600">
              <b>Memberships:</b>
              {{ destinationMembershipNames || "—" }}
            </div>
          </div>
        </Transition>
      </div>

      <i class="fa fa-exchange mx-4 hidden md:block" @click="handleSwap"></i>

      <div class="d-flex flex-column full-width">
        <CustomerAddressSelect
          v-if="currentUser"
          v-model="duplicateId"
          name="duplicateId"
          :vee-validate-errors="v$.$errors"
          :customer="currentUser"
          class="full-width"
          label="Address to merge"
          :filter="(address) => address.id !== destinationId"
          required
          @selected-address="(address) => (duplicateAddress = address)"
        />
        <Transition>
          <div v-if="duplicateAddress" class="card p-3 full-width mt-3">
            <div>
              <b>{{ getAddressName(duplicateAddress) }}</b>
            </div>
            <AddressLabel :address="duplicateAddress" location-type />
            <AddressTaxLabel class="mt-2" :address="duplicateAddress" />
            <div class="mt-2 text-sm text-gray-600">
              <b>Memberships:</b>
              {{ duplicateMembershipNames || "—" }}
            </div>
          </div>
        </Transition>
      </div>
    </div>
    <div
      v-if="destinationAddress && duplicateAddress"
      class="mt-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"
    >
      <i class="fa fa-info-circle mr-1"></i>
      All jobs, quotes, invoices, equipment, contacts, memberships, and tax
      settings will be transferred to the destination address. Duplicate
      contacts and memberships will be consolidated. The merged address will be
      archived.
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Pagination from "@/lib/pagination";

import AddressLabel from "@/components/AddressLabel.vue";
import AddressTaxLabel from "@/components/AddressTaxLabel.vue";
import Modal from "@/components/Modal.vue";
import Transition from "@/components/Transition.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import CustomerAddressSelect from "./AddressSelector/CustomerAddressSelect.vue";

export default {
  components: {
    CustomerAddressSelect,
    AddressLabel,
    Modal,
    Transition,
    AddressTaxLabel
  },
  emits: ["hide-modal"],
  data() {
    return {
      duplicateAddress: null,
      destinationAddress: null,
      destinationId: null,
      duplicateId: null,
      loading: false,
      memberships: []
    };
  },
  computed: {
    ...mapState({
      currentUser: "currentUser"
    }),
    destinationMemberships() {
      return this.membershipsForAddress(this.destinationId);
    },
    duplicateMemberships() {
      return this.membershipsForAddress(this.duplicateId);
    },
    destinationMembershipNames() {
      return this.destinationMemberships
        .map((m) => m.membership_program_name)
        .join(", ");
    },
    duplicateMembershipNames() {
      return this.duplicateMemberships
        .map((m) => m.membership_program_name)
        .join(", ");
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      destinationId: { required, $autoDirty: true },
      duplicateId: { required, $autoDirty: true }
    };
  },
  mounted() {
    this.fetchMemberships();
  },
  methods: {
    ...mapActions([
      "mergeAddresses",
      "confirmDelete",
      "fetchCustomerAddresses"
    ]),
    async fetchMemberships() {
      const url = this.currentUser?.links?.fetch_memberships_paginated;
      if (!url) return;

      try {
        const pager = new Pagination({ baseURL: url, perPage: 999 });
        const response = await pager.getData();
        if (response?.data?.data) {
          this.memberships = response.data.data;
        }
      } catch {
        // Non-critical display data — fail silently
      }
    },
    membershipsForAddress(addressId) {
      if (!addressId) return [];
      return this.memberships.filter((m) => m.address_ids?.includes(addressId));
    },
    filterOptions(options, id) {
      return options
        .filter((address) => address.id !== id)
        .map((address) => ({
          value: address.id,
          label: this.fullAddress(address)
        }));
    },
    findAddress(options, id) {
      return options.find((address) => address.id === id);
    },
    getAddressName(address) {
      if (!address) {
        return "--";
      }
      return `${address.name} ${address.primary ? "(Billing Address)" : ""}`;
    },
    handleSwap() {
      const destination = this.destinationId;
      const dupe = this.duplicateId;
      this.destinationId = dupe;
      this.duplicateId = destination;
    },
    handleSubmit() {
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.confirmDelete({
            message:
              "This merge cannot be undone. Are you sure you wish to continue?",
            title: "Confirm Merge",
            className: "merge-confirm-modal",
            confirmInputValue: "Merge"
          }).then(() => {
            this.mergeAddresses({
              id: this.currentUser.id,
              data: {
                destination_address_id: this.destinationId,
                duplicate_address_ids: [this.duplicateId]
              }
            }).then(() => {
              window.notify.success("Addresses merged succesfully!");
              this.$emit("hide-modal");
            });
          });
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.fa-exchange {
  cursor: pointer;
  margin-top: 30px;
}
</style>
